/**
 * Sezione "Insights AI" dentro la pagina Bot Telegram (versione minimal).
 * Solo due controlli: toggle attivazione + orario invio.
 * Tutto il resto (anteprima, invio test, regole) è stato volutamente rimosso:
 * il titolare richiede il brief direttamente da Telegram con voce o testo
 * ("dammi il brief", "voglio il resoconto", ecc.).
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Clock, TrendingUp } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InsightsAISection({ getAuthHeaders }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [timeDraft, setTimeDraft] = useState("09:30");
  const [soglia, setSoglia] = useState(5);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/insights/settings`, { headers: getAuthHeaders() });
      setData(res.data);
      setTimeDraft(res.data?.settings?.send_time || "09:30");
      setSoglia(res.data?.settings?.food_cost_soglia_pct ?? 5);
    } catch {
      toast.error("Impossibile caricare Insights AI");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const patch = async (body) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/insights/settings`, body, { headers: getAuthHeaders() });
      setData(d => ({ ...d, settings: res.data.settings }));
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Salvataggio fallito");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    const next = !data.settings.enabled;
    const ok = await patch({ enabled: next });
    if (ok) toast.success(next ? "Insights AI attivati" : "Insights AI disattivati");
  };

  const saveTime = async () => {
    if (!/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/.test(timeDraft)) {
      toast.error("Orario non valido (formato HH:MM)");
      return;
    }
    const ok = await patch({ send_time: timeDraft });
    if (ok) toast.success(`Orario aggiornato a ${timeDraft}`);
  };

  const saveSoglia = async () => {
    const v = Number(soglia);
    if (!(v >= 1 && v <= 50)) {
      toast.error("La soglia deve essere tra 1 e 50%");
      return;
    }
    const ok = await patch({ food_cost_soglia_pct: v });
    if (ok) toast.success(`Soglia alert food cost: ${v}%`);
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200/60 bg-white shadow-sm p-5">
        <p className="text-sm text-gray-400">Caricamento Insights AI…</p>
      </div>
    );
  }
  if (!data) return null;

  const { settings, has_telegram_bot } = data;
  if (!has_telegram_bot) return null;

  const enabled = !!settings?.enabled;

  return (
    <div className="rounded-2xl border border-gray-200/60 bg-white shadow-sm p-5" data-testid="insights-ai-section">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-gray-900">Insights AI — brief giornaliero</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Ogni mattina riceverai un riepilogo automatico delle anomalie del giorno prima.
              Puoi chiederlo anche in qualsiasi momento a voce sul bot: <em>"dammi il brief"</em>, <em>"voglio il resoconto"</em>.
            </p>
          </div>
        </div>
        <button
          onClick={toggleEnabled}
          disabled={saving}
          className={`relative inline-flex items-center h-7 rounded-full w-12 transition-colors flex-shrink-0 ${enabled ? "bg-fuchsia-500" : "bg-gray-200"}`}
          data-testid="insights-toggle"
          aria-label="Attiva/disattiva Insights AI"
        >
          <span className={`inline-block w-5 h-5 transform rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>

      {enabled && (
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Clock className="h-4 w-4 text-gray-400" /> Orario invio
          </div>
          <Input
            type="time"
            value={timeDraft}
            onChange={e => setTimeDraft(e.target.value)}
            className="h-9 w-32 rounded-lg text-sm"
            data-testid="insights-time-input"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={saveTime}
            disabled={saving || timeDraft === settings.send_time}
            className="rounded-lg text-xs"
            data-testid="insights-time-save"
          >
            Salva orario
          </Button>
        </div>
      )}

      {enabled && (
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <TrendingUp className="h-4 w-4 text-gray-400" /> Soglia alert food cost
          </div>
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min={1}
              max={50}
              value={soglia}
              onChange={e => setSoglia(e.target.value)}
              className="h-9 w-20 rounded-lg text-sm"
              data-testid="food-cost-soglia-input"
            />
            <span className="text-sm text-gray-500">%</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={saveSoglia}
            disabled={saving || Number(soglia) === (settings.food_cost_soglia_pct ?? 5)}
            className="rounded-lg text-xs"
            data-testid="food-cost-soglia-save"
          >
            Salva soglia
          </Button>
          <p className="w-full text-xs text-gray-400 mt-1">
            Avvisa nel brief quando il costo di materie prime/packaging fa variare il food cost oltre questa percentuale.
          </p>
        </div>
      )}
    </div>
  );
}
